import React, { useState, Suspense, lazy } from 'react'
import { ProfessionalHeader } from './components/ProfessionalHeader'
import { ProfessionalFooter } from './components/ProfessionalFooter'
import { ProfessionalLoading } from './components/ProfessionalLoading'
import { ErrorBoundary } from './components/ErrorBoundary'
import { OfflineBanner } from './components/OfflineIndicator'
import { AnimationPerformanceMonitor } from './utils/animationOptimizer'
import './styles/error-handling.css'

// Lazy load heavy components for better performance
const ProfessionalDemo = lazy(() => 
  import('./pages/ProfessionalDemo').then(module => ({ 
    default: module.ProfessionalDemo 
  }))
)

const SettlementRailDemo = lazy(() => 
  import('./pages/SettlementRailDemo').then(module => ({ 
    default: module.SettlementRailDemo 
  }))
)

const ProviderFailoverDemoPage = lazy(() => 
  import('./pages/ProviderFailoverDemoPage').then(module => ({ 
    default: module.ProviderFailoverDemoPage 
  }))
)

const PaymentsPage = lazy(() => 
  import('./pages/PaymentsPage').then(module => ({ 
    default: module.PaymentsPage 
  }))
)

const AnalyticsPage = lazy(() => 
  import('./pages/AnalyticsPage').then(module => ({ 
    default: module.AnalyticsPage 
  }))
)

const SettingsPage = lazy(() => 
  import('./pages/SettingsPage').then(module => ({ 
    default: module.SettingsPage 
  }))
)

type Page = 'dashboard' | 'payments' | 'settlement-demo' | 'provider-failover' | 'analytics' | 'settings'

export const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('dashboard')
  const shouldReduceAnimations = AnimationPerformanceMonitor.shouldReduceAnimations()
  
  // Debug logging to track page navigation
  React.useEffect(() => {
    console.log('App currentPage changed to:', currentPage)
  }, [currentPage])

  const renderPage = () => {
    switch (currentPage) {
      case 'settlement-demo':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading settlement demo..." showLogo={true} />}>
              <div className="professional-container">
                <div className="professional-card" style={{ marginBottom: '2rem' }}>
                  <button
                    className="professional-btn professional-btn--secondary"
                    onClick={() => setCurrentPage('dashboard')}
                    style={{ marginBottom: '1rem' }}
                  >
                    ← Back to Dashboard
                  </button>
                  <h1 className="professional-card__title">Settlement Rail Demo</h1>
                  <p className="professional-card__subtitle">
                    Interactive demonstration of multi-rail settlement options
                  </p>
                </div>
                <SettlementRailDemo />
              </div>
            </Suspense>
          </ErrorBoundary>
        )
      case 'provider-failover':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading provider failover demo..." showLogo={false} />}>
              <div className="professional-container">
                <div className="professional-card" style={{ marginBottom: '2rem' }}>
                  <button
                    className="professional-btn professional-btn--secondary"
                    onClick={() => setCurrentPage('dashboard')}
                    style={{ marginBottom: '1rem' }}
                  >
                    ← Back to Dashboard
                  </button>
                  <h1 className="professional-card__title">Provider Failover Demo</h1>
                  <p className="professional-card__subtitle">
                    Real-time provider health monitoring and failover scenarios
                  </p>
                </div>
                <ProviderFailoverDemoPage />
              </div>
            </Suspense>
          </ErrorBoundary>
        )
      case 'payments':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading payments..." showLogo={false} />}>
              <PaymentsPage />
            </Suspense>
          </ErrorBoundary>
        )
      case 'analytics':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading analytics..." showLogo={false} />}>
              <AnalyticsPage />
            </Suspense>
          </ErrorBoundary>
        )
      case 'settings':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading settings..." showLogo={false} />}>
              <SettingsPage />
            </Suspense>
          </ErrorBoundary>
        )
      case 'dashboard':
      default:
        return (
          <ErrorBoundary>
            <Suspense fallback={<ProfessionalLoading message="Loading dashboard..." showLogo={true} />}>
              <div className="professional-container">
                <ProfessionalDemo />
                
                {/* Demo Navigation */}
                <div className="professional-card" style={{ marginTop: '2rem' }}>
                  <div className="professional-card__header">
                    <h3 className="professional-card__title">Demo Components</h3>
                    <p className="professional-card__subtitle">
                      Explore individual components and features of the StablePay platform
                    </p>
                  </div>
                  <div className="professional-card__content">
                    <div className="professional-grid professional-grid--cols-3">
                      <button
                        className={`professional-btn professional-btn--primary ${shouldReduceAnimations ? 'no-animations' : ''}`}
                        onClick={() => {
                          console.log('Navigating to settlement-demo')
                          setCurrentPage('settlement-demo')
                        }}
                        style={{ 
                          height: 'auto', 
                          padding: '1.5rem', 
                          flexDirection: 'column', 
                          gap: '0.75rem',
                          textAlign: 'center',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}
                      >
                        <strong style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                          Settlement Rail Selector
                        </strong>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          opacity: 0.9,
                          lineHeight: '1.4',
                          display: 'block'
                        }}>
                          Interactive demo of the settlement rail selection component with FPS, CIPS, and e-CNY options
                        </span>
                      </button>
                      
                      <button
                        className={`professional-btn professional-btn--primary ${shouldReduceAnimations ? 'no-animations' : ''}`}
                        onClick={() => {
                          console.log('Navigating to provider-failover')
                          setCurrentPage('provider-failover')
                        }}
                        style={{ 
                          height: 'auto', 
                          padding: '1.5rem', 
                          flexDirection: 'column', 
                          gap: '0.75rem',
                          textAlign: 'center',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}
                      >
                        <strong style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                          Provider Failover Demo
                        </strong>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          opacity: 0.9,
                          lineHeight: '1.4',
                          display: 'block'
                        }}>
                          Real-time provider health monitoring and automatic failover scenarios
                        </span>
                      </button>
                      
                      <button
                        className={`professional-btn professional-btn--secondary ${shouldReduceAnimations ? 'no-animations' : ''}`}
                        onClick={() => {
                          alert('More component demos are coming soon! This will include enhanced cost calculator, payment interface, and analytics dashboard.')
                        }}
                        style={{ 
                          height: 'auto', 
                          padding: '1.5rem', 
                          flexDirection: 'column', 
                          gap: '0.75rem',
                          textAlign: 'center',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}
                      >
                        <strong style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                          More Components
                        </strong>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          opacity: 0.8,
                          lineHeight: '1.4',
                          display: 'block'
                        }}>
                          Additional component demos including cost calculator and payment interface
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Suspense>
          </ErrorBoundary>
        )
    }
  }

  // Always render with full layout for consistency
  return (
    <ErrorBoundary>
      <div className={`professional-layout ${shouldReduceAnimations ? 'reduced-motion' : ''}`}>
        <OfflineBanner />
        <ProfessionalHeader currentPage={currentPage} onNavigate={setCurrentPage} />
        <main className="professional-main">
          {renderPage()}
        </main>
        <ProfessionalFooter />
      </div>
    </ErrorBoundary>
  )
}